// 셀프 입점(가입 → 조직 만들기 → 무료 요금제) · 출시 알림 신청 — 값 목록은 0091·0092 check 와 한 글자도 다르면 안 된다(migrations 테스트가 맞춰 본다).

import type { OrgSettingKey } from "../org/types";
import type { DefaultTemplate } from "../template/default-template";

export const STAFF_SIZES = ["1", "2-5", "6-10", "11+"] as const;
export type StaffSize = (typeof STAFF_SIZES)[number];
export const STAFF_SIZE_LABEL: Record<StaffSize, string> = { "1": "1명", "2-5": "2~5명", "6-10": "6~10명", "11+": "11명 이상" };

export const WORK_TYPES = ["residential_full", "partial", "commercial", "other"] as const;
export type WorkType = (typeof WORK_TYPES)[number];
export const WORK_TYPE_LABEL: Record<WorkType, string> = {
  residential_full: "주거 전체 리모델링",
  partial: "부분 시공",
  commercial: "상업 공간",
  other: "기타",
};

export const MONTHLY_SITES = ["1-2", "3-5", "6-10", "11+"] as const;
export type MonthlySites = (typeof MONTHLY_SITES)[number];
export const MONTHLY_SITES_LABEL: Record<MonthlySites, string> = { "1-2": "1~2곳", "3-5": "3~5곳", "6-10": "6~10곳", "11+": "11곳 이상" };

export const SIGNUP_SOURCES = ["search", "referral", "community", "ad", "other"] as const;
export type SignupSource = (typeof SIGNUP_SOURCES)[number];
export const SIGNUP_SOURCE_LABEL: Record<SignupSource, string> = {
  search: "검색",
  referral: "지인 소개",
  community: "커뮤니티",
  ad: "광고",
  other: "기타",
};

// DB 가 받는 문서(0091 terms_consent_doc_check) — marketing 은 옛 기록 호환용으로만 남고 새 가입은 쓰지 않는다.
export const TERMS_DOCS = ["terms", "privacy", "marketing"] as const;
export type TermsDoc = (typeof TERMS_DOCS)[number];
// 가입 때 남기는 기록 — 이용약관 동의 · 개인정보 처리방침 확인(둘 다 필수).
export const REQUIRED_TERMS_DOCS = ["terms", "privacy"] as const satisfies readonly TermsDoc[];
// 지금 약관 판 — 문서를 고치면 올린다(0091 terms_consent_version_len 1~40자).
export const TERMS_VERSION = "2026-10-29";

export const PLAN_INTEREST_NEEDS = ["photo", "alimtalk", "tax_invoice", "other"] as const;
export type PlanInterestNeed = (typeof PLAN_INTEREST_NEEDS)[number];
export const PLAN_INTEREST_NEED_LABEL: Record<PlanInterestNeed, string> = {
  photo: "사진·첨부 저장 공간",
  alimtalk: "알림톡 발송",
  tax_invoice: "세금계산서",
  other: "기타",
};

// 0092 check 와 같은 길이.
export const PLAN_INTEREST_MEMO_MAX = 500;
export const PLAN_INTEREST_NAME_MAX = 40;
export const EMAIL_MAX = 254;

// RPC hint 표지(0091 rpc_org_self_create) — 오류는 문구가 아니라 이 표지로 타입을 고른다.
export const ORG_SELF_INVALID_HINT = "org_self_invalid";
export const ORG_SELF_ALREADY_MEMBER_HINT = "org_self_already_member";
export const ORG_SELF_FREE_LIMIT_HINT = "org_self_free_limit";
export const ORG_SELF_SLUG_TAKEN_HINT = "org_self_slug_taken";

// RPC hint 표지(0127 rpc_org_reapply) — 대표 아님 · 반려 아님 · 24시간 전.
export const ORG_REAPPLY_FORBIDDEN_HINT = "org_reapply_forbidden";
export const ORG_REAPPLY_NOT_REJECTED_HINT = "org_reapply_not_rejected";
export const ORG_REAPPLY_TOO_EARLY_HINT = "org_reapply_too_early";

export interface OrgProfileRow {
  id: string;
  org_id: string;
  created_by: string | null;
  staff_size: StaffSize | null;
  work_types: WorkType[];
  monthly_sites: MonthlySites | null;
  source: SignupSource | null;
  created_at: string;
  updated_at: string;
}

export interface TermsConsentRow {
  id: string;
  org_id: string;
  user_id: string;
  doc: TermsDoc;
  version: string;
  agreed: boolean;
  agreed_at: string;
  created_at: string;
}

export interface PlanInterestRow {
  id: string;
  org_id: string;
  member_id: string | null;
  needs: PlanInterestNeed[];
  memo: string | null;
  recipient_name: string;
  email: string;
  created_at: string;
  updated_at: string;
}

// 멤버가 쓰는 일곱 열(0092 열 단위 insert 권한).
export type PlanInterestInsert = Pick<PlanInterestRow, "id" | "org_id" | "member_id" | "needs" | "memo" | "recipient_name" | "email">;

// rpc_org_self_create 인자 한 벌 — id·값·한도·동의 시각은 앱이 다 정해 보낸다.
export interface OrgSelfCreate {
  userId: string;
  org: { id: string; name: string; slug: string; address: string; tel: string; status: "active" | "pending"; status_changed_at: string | null };
  member: { id: string; name: string; email: string; phone: string | null };
  settings: { id: string; key: OrgSettingKey; value: string }[];
  plan: { id: string; plan: "free"; active_site_limit: number | null; storage_limit_bytes: number; notify_month_limit: number };
  profile: { id: string; staff_size: StaffSize | null; work_types: WorkType[]; monthly_sites: MonthlySites | null; source: SignupSource | null };
  consents: { id: string; doc: TermsDoc; version: string; agreed: boolean; agreed_at: string }[];
  activity: { id: string; action: string; summary: string; meta: Record<string, string> };
  template: DefaultTemplate;
}

// rpc_org_reapply 인자 한 벌 — 회사 정보·설정 행 id·지울 설정 키·새 상태 메모·장부 값은 앱이 정한다(주소 slug 는 바꾸지 않는다).
export interface OrgReapply {
  orgId: string;
  userId: string;
  org: { name: string; address: string; tel: string };
  member: { name: string; phone: string | null };
  settings: { id: string; key: OrgSettingKey; value: string }[];
  cleared_keys: OrgSettingKey[];
  profile: { id: string; staff_size: StaffSize | null; work_types: WorkType[]; monthly_sites: MonthlySites | null; source: SignupSource | null };
  status_changed_at: string;
  status_note: string;
  audit_id: string;
  actor_email: string;
  audit_before: { status: "terminated"; status_changed_at: string | null; status_note: string | null };
  audit_after: { status: "pending"; status_changed_at: string; status_note: string };
}

